import React, { useState } from 'react';
import styled from 'styled-components';
import { useWeb3 } from '../contexts/Web3Context';
import { useWallet } from '@solana/wallet-adapter-react';
import { useConnection } from '@solana/wallet-adapter-react';
import * as web3 from '@solana/web3.js';

const TransactionContainer = styled.div`
  background: rgba(30, 30, 40, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 30px;
  margin-top: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: 500px;
  width: 100%;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 20px;
  cursor: pointer;
  padding: 5px;
  line-height: 1;
  
  &:hover {
    color: white;
    transform: scale(1.1);
  }
`;

const Title = styled.h3`
  background: linear-gradient(90deg, #9945FF, #14F195);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  font-size: 24px;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 700;
`;

const TransactionButton = styled.button`
  background: linear-gradient(45deg, #9945FF, #14F195);
  color: white;
  font-size: 16px;
  padding: 14px 24px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 25px;
  transition: all 0.3s;
  width: 100%;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(153, 69, 255, 0.4);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  &:disabled {
    background: #555;
    cursor: not-allowed;
    opacity: 0.7;
    transform: none;
    box-shadow: none;
  }
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.6s;
  }
  
  &:hover:not(:disabled)::before {
    left: 100%;
  }
`;

const TransactionStatus = styled.div<{ success?: boolean; error?: boolean }>`
  margin-top: 20px;
  padding: 15px;
  border-radius: 12px;
  font-size: 15px;
  text-align: center;
  
  ${props => props.success && `
    background-color: rgba(20, 241, 149, 0.1);
    color: #14F195;
    border: 1px solid rgba(20, 241, 149, 0.3);
  `}
  
  ${props => props.error && `
    background-color: rgba(255, 71, 87, 0.1);
    color: #FF4757;
    border: 1px solid rgba(255, 71, 87, 0.3);
  `}
`;

const TransactionLink = styled.a`
  color: #9945FF;
  font-weight: 500;
  text-decoration: underline;
  word-break: break-all;
  transition: all 0.3s;
  
  &:hover {
    color: #14F195;
    text-decoration: none;
  }
`;

interface TransactionDemoProps {
  rewardAmount?: number;
  onClose?: () => void;
}

const TransactionDemo: React.FC<TransactionDemoProps> = ({ rewardAmount = 0.001, onClose }) => {
  const { isConnected, fullAddress } = useWeb3();
  const { publicKey, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<{ message: string; success?: boolean; error?: boolean; signature?: string } | null>(null);
  
  // Game treasury wallet - this would be your project's wallet
  const TREASURY_WALLET = new web3.PublicKey("6rktLCoNh2reWJjVRGZXbUR8gXNdU1yDeLMoND8cRxiQ");
  
  const sendReward = async () => {
    if (!isConnected || !publicKey || !connection || !sendTransaction) {
      setStatus({
        message: "Wallet not connected!",
        error: true
      });
      return;
    }
    
    try {
      setSending(true);
      setStatus({ message: "Preparing transaction..." });
      
      // Convert SOL amount to lamports (1 SOL = 1,000,000,000 lamports)
      const lamports = web3.LAMPORTS_PER_SOL * rewardAmount;
      
      // Create a simple transaction to send SOL from the treasury to the player
      const transaction = new web3.Transaction().add(
        web3.SystemProgram.transfer({
          fromPubkey: TREASURY_WALLET,
          toPubkey: publicKey,
          lamports
        })
      );
      
      // Get recent blockhash
      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;
      
      setStatus({ message: "Sending transaction..." });
      
      // Sign and send transaction
      const signature = await sendTransaction(transaction, connection);
      
      setStatus({ 
        message: "Transaction sent! Confirming...",
      });
      
      // Wait for confirmation
      const confirmation = await connection.confirmTransaction(signature, 'confirmed');
      
      if (confirmation.value.err) {
        setStatus({
          message: `Transaction failed: ${confirmation.value.err}`,
          error: true
        });
      } else {
        setStatus({
          message: `Successfully sent ${rewardAmount} SOL reward!`,
          success: true,
          signature
        });
      }
    } catch (error) {
      console.error('Error sending transaction:', error);
      setStatus({
        message: `Error sending transaction: ${error instanceof Error ? error.message : String(error)}`,
        error: true
      });
    } finally {
      setSending(false);
    }
  };
  
  const getExplorerLink = (signature: string) => {
    // Use devnet or mainnet explorer based on your app configuration
    return `https://explorer.solana.com/tx/${signature}?cluster=devnet`;
  };
  
  return (
    <TransactionContainer>
      {onClose && (
        <CloseButton onClick={onClose}>×</CloseButton>
      )}
      <Title>Tariff Score</Title>
      <p style={{ textAlign: 'center', marginBottom: '20px', color: 'rgba(255, 255, 255, 0.8)' }}>
        Claim your reward of {rewardAmount} SOL for completing the game!
      </p>
      
      <TransactionButton 
        onClick={sendReward} 
        disabled={!isConnected || sending}
      >
        {sending ? 'Processing...' : 'Claim Reward'}
      </TransactionButton>
      
      {status && (
        <TransactionStatus success={status.success} error={status.error}>
          {status.message}
          {status.signature && (
            <div style={{ marginTop: '10px' }}>
              <TransactionLink 
                href={getExplorerLink(status.signature)} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                View on Solana Explorer
              </TransactionLink>
            </div>
          )}
        </TransactionStatus>
      )}
    </TransactionContainer>
  );
};

export default TransactionDemo; 